import { t } from "i18next";
import { FC, useCallback, useContext, useEffect, useState } from "react";

import FormWrapper from "@app/components/FormWrapper";
import SystemConfigurationForm from "@app/components/forms/SystemConfiguration.form";
import {
    addSystemConfiguration,
    getSystemConfiguration,
} from "@app/services/Endpoints/SystemConfiguration";
import { ModalType, SystemConfigurationErrors } from "@app/types/Common.types";
import { SystemConfigurationFormData } from "@app/types/SystemConfiguration";
import { GlobalEnum } from "@app/utils/constant";
import { FormContext } from "@app/utils/contexts/Form.context";

const initialFormData: SystemConfigurationFormData = {
    delayTime: "",
    outOfQueue: false,
    wastage: false,
    bulk: false,
    bulkBuffer: "",
    optimizeClubbing: false,
    leaderboard: true,
    HTThreshold: "",
    consumeOverdue: false,
    globalEPT: false,
    bulkAutoActionMode: "",
    bulkAutoActionThreshold: "",
    globalForecastedCookingBatch: false,
    dynamicOffset: false,
};

interface SystemConfigurationContainerProps {
    systemConfiguration?: SystemConfigurationFormData;
}

const SystemConfigurationContainer: FC<SystemConfigurationContainerProps> = ({
    systemConfiguration,
}) => {
    const { triggerShowModal, setActions, isDirty, setIsDirty } = useContext(FormContext);
    const [newSystemConfiguration, setNewSystemConfiguration] =
        useState<SystemConfigurationFormData>(initialFormData);

        const [errors, setErrors] = useState<Partial<SystemConfigurationErrors>>();

    const fetchSystemConfiguration = useCallback(() => {
        getSystemConfiguration()
            .then((res) => {
                if (res) {
                    const delayTime =
                        res.data.data.find((d) => d.name === "SERVED_BUTTON_DELAY_TIME")?.value ??
                        "0";
                    const bulkBuffer =
                        res.data.data.find((d) => d.name === "BULK_BUFFER")?.value ?? "0";
                    const outOfQueue =
                        res.data.data
                            .find((d) => d.name === "OUT_OF_QUEUE")
                            ?.value?.toLowerCase() === "true";
                    const wastage =
                        res.data.data.find((d) => d.name === "WASTAGE")?.value?.toLowerCase() ===
                        "true";
                    const bulk =
                        res.data.data.find((d) => d.name === "BULK")?.value?.toLowerCase() ===
                        "true";
                    const optimizeClubbing =
                        res.data.data
                            .find((d) => d.name === "OPTIMIZE_CLUBBING")
                            ?.value?.toLowerCase() === "true";
                    const leaderboard =
                        res.data.data
                            .find((d) => d.name === "LEADERBOARD")
                            ?.value?.toLowerCase() === "true";
                    const HTThreshold =
                        res.data.data.find((d) => d.name === "HT_THRESHOLD")?.value ?? "100";
                    const consumeOverdue =
                        res.data.data
                            .find((d) => d.name === "CONSUME_OVERDUE_ADMIN")
                            ?.value?.toLowerCase() === "true";
                    const globalEPT =
                        res.data.data
                            .find((d) => d.name === GlobalEnum.GLOBAL_EPT)
                            ?.value?.toLowerCase() === "true";
                    const bulkAutoActionMode =
                        res.data.data.find((d) => d.name === "BULK_AUTO_ACTION_MODE")?.value ?? "1";
                    const bulkAutoActionThreshold =
                        res.data.data.find((d) => d.name === "BULK_AUTO_ACTION_THRESHOLD")?.value ??
                        "";
                    const globalForecastedCookingBatch =
                        res.data.data
                            .find((d) => d.name === "GLOBAL_FORECASTED_COOKING_BATCH")
                            ?.value?.toLowerCase() === "true";
                    const dynamicOffset =
                        res.data.data
                            .find((d) => d.name === "DYNAMIC_OFFSET")
                            ?.value?.toLowerCase() === "true";
                    setNewSystemConfiguration({
                        delayTime,
                        outOfQueue,
                        wastage,
                        bulk,
                        bulkBuffer,
                        optimizeClubbing,
                        leaderboard,
                        HTThreshold,
                        consumeOverdue,
                        globalEPT,
                        bulkAutoActionMode,
                        bulkAutoActionThreshold,
                        globalForecastedCookingBatch,
                        dynamicOffset,
                    });
                } else {
                    setNewSystemConfiguration({ ...initialFormData });
                }
            })
            .catch(() => setNewSystemConfiguration({ ...initialFormData }));
    }, []);

    useEffect(() => {
        fetchSystemConfiguration();
    }, [fetchSystemConfiguration]);

    useEffect(() => {
        if (systemConfiguration) {
            const {
                delayTime,
                outOfQueue,
                wastage,
                bulk,
                bulkBuffer,
                optimizeClubbing,
                leaderboard,
                HTThreshold,
                consumeOverdue,
                globalEPT,
                bulkAutoActionMode,
                bulkAutoActionThreshold,
                globalForecastedCookingBatch,
                dynamicOffset,
            } = {
                ...systemConfiguration,
            };

            setNewSystemConfiguration({
                delayTime,
                outOfQueue,
                wastage,
                bulk,
                bulkBuffer,
                optimizeClubbing,
                leaderboard,
                HTThreshold,
                consumeOverdue,
                globalEPT,
                bulkAutoActionMode,
                bulkAutoActionThreshold,
                globalForecastedCookingBatch,
                dynamicOffset,
            });
        }
    }, [systemConfiguration]);

    const handleChange = (name: string, value: string | boolean) => {
        setIsDirty(true);
        const data: SystemConfigurationFormData = { ...newSystemConfiguration };
        if (errors?.[name]) {
            setErrors({ ...errors, [name]: "" });
        }
        setNewSystemConfiguration({ ...data, [name]: value });
    };

    const handleCreateSystemConfiguration = () => {
        const { bulkBuffer, ...formData } = newSystemConfiguration;

        addSystemConfiguration(
            newSystemConfiguration.bulk ? { ...newSystemConfiguration } : { ...formData },
        )
            .then(() => {
                triggerShowModal(false, ModalType.SUBMIT);
                setIsDirty(false);
            })
            .catch(() => {
                triggerShowModal(false, ModalType.SUBMIT);
            });
    };

    const handleSubmit = () => {
        const {
            delayTime,
            bulkBuffer,
            bulk,
            optimizeClubbing,
            HTThreshold,
            bulkAutoActionThreshold,
        } = newSystemConfiguration;
        const isDelayTimeValid = !!delayTime;
        const isBulkBuffer = !!bulkBuffer;
        const isBulkBufferGreater = isBulkBuffer && Number(bulkBuffer) > 0;
        const isBulkBufferSmaller = isBulkBuffer && Number(bulkBuffer) <= 100;
        const isBulkBufferValid = bulk ? isBulkBufferGreater && isBulkBufferSmaller : true;
        const isHTThreshold = !!HTThreshold;
        const isHTThresholdGreater = isHTThreshold && Number(HTThreshold) >= 0;
        const isHTThresholdSmaller = isHTThreshold && Number(HTThreshold) <= 200;
        const isHTThresholdValid = optimizeClubbing
            ? isHTThresholdGreater && isHTThresholdSmaller
            : true;
        const isBulkAutoActionThresholdGreater =
            Number(bulkAutoActionThreshold) < 1 || Number(bulkAutoActionThreshold) > 99;
        const isBulkAutoActionThresholdEmpty = !!bulkAutoActionThreshold;
        const isBulkAutoActionThresholdValid =
            isBulkAutoActionThresholdEmpty && !isBulkAutoActionThresholdGreater;
        const isDataValid =
            isDelayTimeValid &&
            isBulkBufferValid &&
            isHTThresholdValid &&
            isBulkAutoActionThresholdValid;

        if (isDataValid) {
            setErrors(undefined);
            triggerShowModal(true, ModalType.SUBMIT);
            setActions({
                done: () => handleCreateSystemConfiguration(),
                cancel: () => triggerShowModal(false, ModalType.SUBMIT),
            });
        } else {
            const currentErrors: Partial<SystemConfigurationErrors> = {};
            if (!isDelayTimeValid)
                currentErrors.delayTime = `${t("ERRORS.REQUIRED", {
                    name: "Delay Time",
                })}`;
            if (!isBulkBufferValid)
                if (isBulkBufferGreater)
                    currentErrors.bulkBuffer = "Buffer value should not be greater than 100";
                else if (isBulkBufferSmaller)
                    currentErrors.bulkBuffer = "Buffer value should be greater than 0";
                else
                    currentErrors.bulkBuffer = `${t("ERRORS.REQUIRED", {
                        name: "Bulk Buffer",
                    })}`;
            if (!isHTThresholdValid)
                if (isHTThresholdGreater)
                    currentErrors.HTThreshold = "Threshold value should not be greater than 200";
                else if (isHTThresholdSmaller)
                    currentErrors.HTThreshold = "Threshold value should be greater than 0";
                else
                    currentErrors.HTThreshold = `${t("ERRORS.REQUIRED", {
                        name: "Threshold for HT",
                    })}`;
            if (!isBulkAutoActionThresholdValid) {
                if (!isBulkAutoActionThresholdEmpty) {
                    currentErrors.bulkAutoActionThreshold = `${t("ERRORS.REQUIRED", {
                        name: "Bulk Auto Action Threshold",
                    })}`;
                } else if (isBulkAutoActionThresholdGreater) {
                    currentErrors.bulkAutoActionThreshold = `${t("ERRORS.IN_BETWEEN_1_99")}`;
                }
            }
            setErrors({ ...errors, ...currentErrors });
        }
    };
    return (
        <FormWrapper
            heading={"Fill in details for system configurations"}
            onSubmitClick={handleSubmit}
            submitDisabled={!isDirty}
            noteSection={true}
            noteText='Note: Any changes made on this screen will be applicable from next
                            working day!'
        >
            <SystemConfigurationForm
                data={newSystemConfiguration}
                onChange={handleChange}
                errors={errors}
            />
        </FormWrapper>
    );
};

export default SystemConfigurationContainer;
